"use client";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ClaimStatus from "../../../global/view/ClaimStatus";
import PatientSignatureModal from "../../../global/view/patientSignature";
import DoctorePrescription from "../../../global/view/doctorePrescription";

export default function ClaimStatusView({ scriptData, claimDetails }) {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const finalScriptData = scriptData || state.scriptData || {};
  const backToPath = state.returnToPath || state.fromPath || state.originPath || state.backToPath || null;
  const defaultReturnPath = location.pathname.includes("/amend-script/claim-status")
    ? "/customer/dispensary/amend-script/payment-summary"
    : "/customer/dispensary/script/payment-summary";
  const [progressPercent, setProgressPercent] = useState(
    Number((claimDetails || state.claimDetails)?.progressPercent ?? 70)
  );

  // Resume signature flow state (like ChronicScriptView)
  const [showPatientSig, setShowPatientSig] = useState(false);
  const [pharmacistSig, setPharmacistSig] = useState(state.pharmacistSignature || null);
  const [patientSig, setPatientSig] = useState(null);
  const [showDoctorModal, setShowDoctorModal] = useState(false);

  // If returning from ClaimApprovedTable: skip progress animation and open patient signature
  useEffect(() => {
    if (state.continueAfterMedicalAid) {
      // proceed directly to patient signature
      setShowPatientSig(true);
      return;
    }
    // Otherwise animate progress and navigate to Claim Approved on complete
    if (progressPercent >= 100) {
      navigate("/customer/dispensary/claim-approved", {
        replace: true, // replace ClaimStatus in history to avoid seeing it on Back
        state: { scriptData: finalScriptData, returnToPath: backToPath || defaultReturnPath },
      });
      return;
    }
    const t = setInterval(() => {
      setProgressPercent((p) => Math.min(100, p + 5));
    }, 500);
    return () => clearInterval(t);
  }, [progressPercent, navigate, finalScriptData, location.pathname, backToPath, state.continueAfterMedicalAid]);

  return (
    <>
      {/* Show ClaimStatus only if not in resume flow */}
      {!state.continueAfterMedicalAid && (
        <ClaimStatus
          isOpen={true}
          onClose={() => navigate(-1)}
          scriptData={finalScriptData}
          claimDetails={{ ...(claimDetails || state.claimDetails), progressPercent }}
        />
      )}

      {/* Patient signature then doctor prescription */}
      <PatientSignatureModal
        isOpen={!!state.continueAfterMedicalAid && showPatientSig}
        onClose={() => setShowPatientSig(false)}
        onConfirm={(dataUrl) => {
          setPatientSig(dataUrl);
          setShowPatientSig(false);
          setShowDoctorModal(true);
        }}
        initialSignature={patientSig}
        totals={{
          amount: finalScriptData?.amount ?? "R129.18",
          vat: finalScriptData?.vat ?? "R19.38",
          total: finalScriptData?.total
            ? `R${finalScriptData.total.toFixed?.(2) ?? finalScriptData.total}`
            : "R148.56",
        }}
      />

      <DoctorePrescription
        isOpen={!!state.continueAfterMedicalAid && showDoctorModal}
        onClose={() => setShowDoctorModal(false)}
        pharmacistSignature={pharmacistSig}
        patientSignature={patientSig}
        totals={{
          vat: finalScriptData?.vat ?? "R19.38",
          total: finalScriptData?.total
            ? `R${finalScriptData.total.toFixed?.(2) ?? finalScriptData.total}`
            : "R148.56",
        }}
        useCompletedPopup={true}
      />
    </>
  );
}